import PropTypes from 'prop-types';
import { useRef, useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Divider from '@mui/material/Divider';
import Checkbox from '@mui/material/Checkbox';
import Skeleton from '@mui/material/Skeleton';
import TextField from '@mui/material/TextField';
import AlertTitle from '@mui/material/AlertTitle';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import DialogTitle from '@mui/material/DialogTitle';
import Autocomplete from '@mui/material/Autocomplete';
import { alpha, useTheme } from '@mui/material/styles';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import useMediaQuery from '@mui/material/useMediaQuery';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import FormControlLabel from '@mui/material/FormControlLabel';

import { useDebounce } from 'src/hooks/use-debounce';

import { ICD_ERROR } from 'src/services/ICD/icd-client';
import { esCodigoCie } from 'src/services/ICD/icd-normalize';
import { obtenerRelease } from 'src/services/ICD/icd-release';
import {
  obtenerHijos,
  obtenerDetalle,
  buscarPorCodigo,
  obtenerCapitulos,
  buscarDiagnosticos,
} from 'src/services/ICD/icd-search-service';

import Iconify from 'src/components/iconify';

import IcdOptionRow from './icd-option-row';
import IcdEntityDetail from './icd-entity-detail';

// ----------------------------------------------------------------------
// Catálogo CIE-11 completo: buscador con filtros a la izquierda y detalle de
// la entidad resaltada a la derecha. Sustituye al popup de la OMS.
//
// El detalle se carga al RESALTAR (flechas, clic o ratón), no al confirmar,
// para poder comparar definiciones sin perder el sitio. Pulsar un ancestro de
// la ruta lista sus hijos en la izquierda: eso lo hace explorable.
// ----------------------------------------------------------------------

const MINIMO_CARACTERES = 3;

const RETARDO_BUSQUEDA = 250;

// El resaltado por ratón dispararía una petición por fila barrida; este
// retardo hace que solo se pida el detalle de la fila en la que se descansa.
const RETARDO_DETALLE = 180;

const ANCHO_DETALLE = 400;

const FUENTE_MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

const EXPLORACION_VACIA = Object.freeze({
  id: null,
  codigo: '',
  titulo: '',
  hijos: [],
  cargando: false,
  error: null,
});

const barraFina = (theme) => ({
  '&::-webkit-scrollbar': { width: 8 },
  '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    borderRadius: 4,
    backgroundColor: alpha(theme.palette.grey[500], 0.32),
  },
});

// El detalle no trae los flags de residual; en MMS el sufijo del código sí.
const diagnosticoDesdeDetalle = (detalle) =>
  detalle
    ? {
        id: detalle.id,
        codigo: detalle.codigo,
        titulo: detalle.titulo,
        tituloHtml: detalle.titulo,
        capitulo: '',
        esHoja: Boolean(detalle.esHoja),
        puntaje: 0,
        sinonimoCoincidente: null,
        esResidualNoEspecificado: /\.Z$/i.test(detalle.codigo || ''),
        esResidualOtro: /\.Y$/i.test(detalle.codigo || ''),
      }
    : null;

// ----------------------------------------------------------------------

function EstadoVacio({ icono, titulo, descripcion }) {
  return (
    <Stack
      spacing={1}
      alignItems="center"
      justifyContent="center"
      sx={{ px: 3, py: 6, height: 1, textAlign: 'center' }}
    >
      <Iconify width={38} icon={icono} sx={{ color: 'text.disabled' }} />

      <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
        {titulo}
      </Typography>

      {!!descripcion && (
        <Typography variant="caption" sx={{ maxWidth: 320, color: 'text.disabled' }}>
          {descripcion}
        </Typography>
      )}
    </Stack>
  );
}

EstadoVacio.propTypes = {
  descripcion: PropTypes.string,
  icono: PropTypes.string.isRequired,
  titulo: PropTypes.string.isRequired,
};

// ----------------------------------------------------------------------

// Un fallo del servicio jamás puede parecer "no existe ese diagnóstico".
function EstadoError({ error, onReintentar }) {
  return (
    <Alert
      severity="error"
      sx={{ m: 2 }}
      action={
        <Button
          size="small"
          color="error"
          onClick={onReintentar}
          startIcon={<Iconify width={16} icon="eva:refresh-fill" />}
        >
          Reintentar
        </Button>
      }
    >
      <AlertTitle sx={{ mb: 0.25 }}>No se pudo consultar el catálogo</AlertTitle>
      <Typography variant="caption">{error?.message}</Typography>
    </Alert>
  );
}

EstadoError.propTypes = {
  error: PropTypes.object,
  onReintentar: PropTypes.func,
};

// ----------------------------------------------------------------------

function CargandoLista() {
  return (
    <Stack spacing={1} sx={{ p: 1 }}>
      {[0, 1, 2, 3, 4, 5].map((fila) => (
        <Stack key={fila} spacing={1.25} direction="row" alignItems="center">
          <Skeleton variant="rounded" width={76} height={22} sx={{ flexShrink: 0 }} />
          <Skeleton variant="text" height={22} sx={{ flexGrow: 1 }} />
        </Stack>
      ))}
    </Stack>
  );
}

// ----------------------------------------------------------------------

export default function IcdAdvancedDialog({
  abierto = false,
  onCerrar,
  consultaInicial = '',
  idInicial = null,
  onSeleccionar,
}) {
  const theme = useTheme();
  const esMovil = useMediaQuery(theme.breakpoints.down('md'));

  const [consulta, setConsulta] = useState('');
  const [capitulo, setCapitulo] = useState('');
  const [soloHojas, setSoloHojas] = useState(false);

  const [resultados, setResultados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [intento, setIntento] = useState(0);

  const [modo, setModo] = useState('busqueda');
  const [exploracion, setExploracion] = useState(EXPLORACION_VACIA);

  const [resaltado, setResaltado] = useState(null);
  const [expandidoMovil, setExpandidoMovil] = useState(null);
  const [capitulos, setCapitulos] = useState([]);
  const [release, setRelease] = useState('');

  const refsFilas = useRef({});
  const refControlRama = useRef(null);
  const desdeTeclado = useRef(false);

  const consultaDebounced = useDebounce(consulta, RETARDO_BUSQUEDA);
  const idResaltado = resaltado?.id ?? null;
  const idDetalle = useDebounce(idResaltado, RETARDO_DETALLE);

  // El filtro sigue guardándose como código (es lo que espera la búsqueda);
  // el Autocomplete necesita el objeto, y null cuando no hay filtro.
  const capituloSeleccionado = capitulo
    ? capitulos.find((item) => item.codigo === capitulo) || null
    : null;

  // --------------------------------------------------------------------
  // Exploración del árbol
  // --------------------------------------------------------------------

  const explorar = useCallback(async (id, signalExterna) => {
    if (!id) return;

    refControlRama.current?.abort();
    const control = new AbortController();
    refControlRama.current = control;
    const signal = signalExterna ?? control.signal;

    setModo('exploracion');
    setExploracion({ ...EXPLORACION_VACIA, id, cargando: true });

    try {
      // Secuencial a propósito: `obtenerHijos` reusa el detalle ya cacheado.
      // Es aquí, al navegar a la rama, donde se paga la resolución de los hijos.
      const detalle = await obtenerDetalle(id, { signal });
      const hijos = await obtenerHijos(id, { signal });
      if (signal.aborted) return;

      // Que fallen TODAS las resoluciones no es "no tiene subcategorías": ese
      // vacío se lee como un dato del catálogo y no lo es.
      const fallaronHijos = (detalle?.totalHijos ?? 0) > 0 && hijos.length === 0;

      setExploracion({
        id,
        codigo: detalle?.codigo ?? '',
        titulo: detalle?.titulo ?? '',
        hijos,
        cargando: false,
        error: fallaronHijos
          ? new Error('No se pudieron cargar las subcategorías de esta entidad.')
          : null,
      });
      setResaltado(diagnosticoDesdeDetalle(detalle));
    } catch (fallo) {
      if (fallo?.tipo === ICD_ERROR.CANCELADO) return;
      setExploracion((previo) => ({ ...previo, cargando: false, error: fallo }));
    }
  }, []);

  // Una hoja no tiene hijos que listar: se abre la rama del padre para que el
  // médico vea la entidad resaltada entre sus hermanas.
  const abrirRama = useCallback(
    async (id, signal) => {
      try {
        const detalle = await obtenerDetalle(id, { signal });
        if (!detalle || signal?.aborted) return;

        const padre = detalle.ruta?.[detalle.ruta.length - 2];
        // `totalHijos` cuenta las URIs sin resolverlas: saber si hay rama no
        // debe costar una petición por hijo.
        const objetivo = detalle.totalHijos ? id : padre?.id;

        if (objetivo) await explorar(objetivo, signal);
        if (signal?.aborted) return;

        setResaltado(diagnosticoDesdeDetalle(detalle));
      } catch (fallo) {
        if (fallo?.tipo === ICD_ERROR.CANCELADO) return;
        setExploracion((previo) => ({ ...previo, cargando: false, error: fallo }));
      }
    },
    [explorar]
  );

  const volverABusqueda = useCallback(() => {
    refControlRama.current?.abort();
    setExploracion(EXPLORACION_VACIA);
    setModo('busqueda');
  }, []);

  // --------------------------------------------------------------------
  // Carga
  // --------------------------------------------------------------------

  useEffect(() => {
    if (!abierto) return undefined;

    const control = new AbortController();

    setConsulta(consultaInicial || '');
    setExpandidoMovil(null);
    setResaltado(null);
    setExploracion(EXPLORACION_VACIA);
    setModo(idInicial ? 'exploracion' : 'busqueda');

    if (idInicial) abrirRama(idInicial, control.signal);

    // Al cerrar hay que abortar TAMBIÉN la rama en vuelo: explorar() crea su
    // propio controlador y su resolución de hijos son decenas de GET.
    return () => {
      control.abort();
      refControlRama.current?.abort();
    };
  }, [abierto, idInicial, consultaInicial, abrirRama]);

  useEffect(() => {
    if (!abierto) return undefined;

    let vivo = true;
    const control = new AbortController();

    obtenerRelease().then((valor) => {
      if (vivo) setRelease(valor);
    });

    // El filtro por capítulo es una comodidad: si falla se busca en todos.
    obtenerCapitulos({ signal: control.signal })
      .then((lista) => {
        if (vivo) setCapitulos(lista);
      })
      .catch(() => {});

    return () => {
      vivo = false;
      control.abort();
    };
  }, [abierto]);

  useEffect(() => {
    if (!abierto || modo !== 'busqueda') return undefined;

    const texto = (consultaDebounced || '').trim();

    if (texto.length < MINIMO_CARACTERES) {
      setResultados([]);
      setError(null);
      setCargando(false);
      return undefined;
    }

    const control = new AbortController();

    setCargando(true);
    setError(null);

    (async () => {
      try {
        const lista = await buscarDiagnosticos(texto, {
          signal: control.signal,
          capitulo: capitulo || undefined,
          soloHojas,
        });

        // /search no encuentra códigos: el exacto llega por /codeinfo y se ancla arriba.
        let finales = lista;
        if (esCodigoCie(texto)) {
          const exacto = await buscarPorCodigo(texto, { signal: control.signal });
          if (exacto) finales = [exacto, ...lista.filter((item) => item.id !== exacto.id)];
        }

        if (control.signal.aborted) return;

        setResultados(finales);
        setCargando(false);
        setResaltado((previo) => (finales.some((item) => item.id === previo?.id) ? previo : null));
      } catch (fallo) {
        if (fallo?.tipo === ICD_ERROR.CANCELADO) return;
        setResultados([]);
        setError(fallo);
        setCargando(false);
      }
    })();

    return () => control.abort();
  }, [abierto, modo, consultaDebounced, capitulo, soloHojas, intento]);

  // --------------------------------------------------------------------
  // Interacción
  // --------------------------------------------------------------------

  const lista = modo === 'busqueda' ? resultados : exploracion.hijos;
  const cargandoLista = modo === 'busqueda' ? cargando : exploracion.cargando;
  const errorLista = modo === 'busqueda' ? error : exploracion.error;
  const textoConsulta = (consultaDebounced || '').trim();

  useEffect(() => {
    if (!desdeTeclado.current) return;
    desdeTeclado.current = false;
    refsFilas.current[idResaltado]?.scrollIntoView({ block: 'nearest' });
  }, [idResaltado]);

  const confirmar = useCallback(
    (diagnostico) => {
      if (!diagnostico) return;
      onSeleccionar?.(diagnostico);
      onCerrar?.();
    },
    [onSeleccionar, onCerrar]
  );

  const resaltarConRaton = (diagnostico) => {
    desdeTeclado.current = false;
    if (diagnostico.id !== idResaltado) setResaltado(diagnostico);
  };

  const moverResaltado = (paso) => {
    if (!lista.length) return;

    const indice = lista.findIndex((item) => item.id === idResaltado);
    let siguiente;

    if (indice < 0) siguiente = paso > 0 ? 0 : lista.length - 1;
    else siguiente = Math.min(lista.length - 1, Math.max(0, indice + paso));

    desdeTeclado.current = true;
    setResaltado(lista[siguiente]);
  };

  const alPulsarTecla = (evento) => {
    if (evento.key === 'ArrowDown') {
      evento.preventDefault();
      moverResaltado(1);
    } else if (evento.key === 'ArrowUp') {
      evento.preventDefault();
      moverResaltado(-1);
    } else if (evento.key === 'Enter' && resaltado) {
      evento.preventDefault();
      confirmar(resaltado);
    }
  };

  // Volver a búsqueda siempre aborta la rama: si no, una exploración lenta
  // terminaba después y resaltaba su entidad sobre los resultados nuevos.
  const alEscribir = (evento) => {
    setConsulta(evento.target.value);
    volverABusqueda();
  };

  const reintentar = () => {
    if (modo === 'busqueda') setIntento((valor) => valor + 1);
    else explorar(exploracion.id);
  };

  // --------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------

  const renderFilas = lista.map((diagnostico) => {
    // El !! importa: `idResaltado` y `expandidoMovil` arrancan en null y una
    // entidad sin id numérico saldría resaltada y abierta sola.
    const esResaltado = !!diagnostico.id && diagnostico.id === idResaltado;

    return (
      <Box
        key={diagnostico.id}
        ref={(nodo) => {
          refsFilas.current[diagnostico.id] = nodo;
        }}
        onClick={() => resaltarConRaton(diagnostico)}
        onMouseEnter={() => resaltarConRaton(diagnostico)}
        onDoubleClick={() => confirmar(diagnostico)}
        sx={{
          cursor: 'pointer',
          borderRadius: 1,
          bgcolor: esResaltado ? alpha(theme.palette.primary.main, 0.1) : 'transparent',
          transition: theme.transitions.create(['background-color'], {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <IcdOptionRow
          consulta={consulta}
          diagnostico={diagnostico}
          onUsar={confirmar}
          expandida={esMovil && !!diagnostico.id && expandidoMovil === diagnostico.id}
          onAlternarDetalle={() => {
            // En móvil no hay panel derecho: el detalle se abre dentro de la fila.
            if (esMovil) {
              setExpandidoMovil((previo) => (previo === diagnostico.id ? null : diagnostico.id));
            }
            resaltarConRaton(diagnostico);
          }}
        />
      </Box>
    );
  });

  const renderContenidoLista = () => {
    if (errorLista) return <EstadoError error={errorLista} onReintentar={reintentar} />;

    if (cargandoLista) return <CargandoLista />;

    if (lista.length > 0) return <Stack spacing={0.25}>{renderFilas}</Stack>;

    if (modo === 'exploracion') {
      return (
        <EstadoVacio
          icono="eva:award-outline"
          titulo="Esta entidad no tiene subcategorías"
          descripcion="Es un código final del catálogo. Úsalo tal cual o vuelve a los resultados de búsqueda."
        />
      );
    }

    if (!textoConsulta) {
      return (
        <EstadoVacio
          icono="eva:search-outline"
          titulo="Busca un diagnóstico"
          descripcion="Escribe el nombre, un sinónimo coloquial o el código CIE-11. También puedes recorrer el catálogo desde la ruta del panel derecho."
        />
      );
    }

    if (textoConsulta.length < MINIMO_CARACTERES) {
      return (
        <EstadoVacio
          icono="eva:edit-2-outline"
          titulo="Escribe al menos 3 letras"
          descripcion="El catálogo busca por palabras, así que necesita algo más de texto."
        />
      );
    }

    return (
      <EstadoVacio
        icono="eva:question-mark-circle-outline"
        titulo={`Sin coincidencias para «${textoConsulta}»`}
        descripcion="Prueba con otra palabra, quita el filtro de capítulo o desmarca «solo códigos facturables»."
      />
    );
  };

  const renderPanelDetalle = (
    <Stack
      sx={{
        width: ANCHO_DETALLE,
        flexShrink: 0,
        bgcolor: alpha(theme.palette.grey[500], 0.04),
      }}
    >
      {resaltado ? (
        <>
          <Stack
            spacing={0.75}
            sx={{
              px: 2,
              py: 1.75,
              borderBottom: `dashed 1px ${theme.palette.divider}`,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                variant="soft"
                color="primary"
                label={resaltado.codigo || 'Sin código'}
                sx={{ fontFamily: FUENTE_MONO, fontVariantNumeric: 'tabular-nums' }}
              />

              {!resaltado.esHoja && (
                <Chip size="small" variant="soft" label="Categoría" color="default" />
              )}
            </Stack>

            <Typography variant="subtitle2">{resaltado.titulo}</Typography>
          </Stack>

          <Box sx={{ p: 2, flex: 1, minHeight: 0, overflowY: 'auto', ...barraFina(theme) }}>
            <IcdEntityDetail id={idDetalle} onNavegar={explorar} />
          </Box>
        </>
      ) : (
        <EstadoVacio
          icono="eva:file-text-outline"
          titulo="Ningún diagnóstico resaltado"
          descripcion="Recorre la lista con las flechas o pasa el ratón por un resultado para ver aquí su definición, sinónimos, exclusiones y clasificación."
        />
      )}
    </Stack>
  );

  return (
    <Dialog
      open={abierto}
      onClose={onCerrar}
      fullWidth
      maxWidth="lg"
      fullScreen={esMovil}
      PaperProps={{ sx: { height: esMovil ? 1 : '82vh' } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Stack spacing={0.25} sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6">Buscar diagnóstico CIE-11</Typography>

            <Typography variant="caption" sx={{ color: 'text.disabled' }}>
              {release
                ? `Clasificación Internacional de Enfermedades · versión ${release} · español`
                : 'Clasificación Internacional de Enfermedades'}
            </Typography>
          </Stack>

          <IconButton size="small" onClick={onCerrar} aria-label="Cerrar">
            <Iconify width={20} icon="eva:close-fill" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        dividers
        onKeyDown={alPulsarTecla}
        sx={{ p: 0, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}
      >
        <Stack spacing={1.5} sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          <TextField
            autoFocus
            fullWidth
            value={consulta}
            onChange={alEscribir}
            placeholder="Ej.: presión alta, neumonía, apen…, BA00.Z"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify width={20} icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
              endAdornment: consulta ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setConsulta('')} aria-label="Limpiar">
                    <Iconify width={18} icon="eva:close-circle-fill" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          <Stack
            spacing={1.5}
            alignItems={{ sm: 'center' }}
            direction={{ xs: 'column', sm: 'row' }}
          >
            {/* Son 28 capítulos con títulos largos: el desplegable obliga a leerlos
                todos. El autocompletado filtra por código o por texto del título. */}
            <Autocomplete
              size="small"
              options={capitulos}
              sx={{ minWidth: 280 }}
              value={capituloSeleccionado}
              noOptionsText="Sin capítulos que coincidan"
              onChange={(evento, opcion) => {
                setCapitulo(opcion?.codigo || '');
                volverABusqueda();
              }}
              getOptionLabel={(opcion) =>
                opcion.codigo ? `${opcion.codigo} · ${opcion.titulo}` : opcion.titulo
              }
              isOptionEqualToValue={(opcion, elegida) => opcion.id === elegida.id}
              renderOption={(props, opcion) => (
                <Stack
                  {...props}
                  spacing={1.25}
                  component="li"
                  direction="row"
                  key={opcion.id}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 34,
                      flexShrink: 0,
                      fontWeight: 600,
                      textAlign: 'center',
                      fontFamily: FUENTE_MONO,
                      color: 'text.secondary',
                    }}
                  >
                    {opcion.codigo || '—'}
                  </Box>

                  <Typography variant="body2" noWrap>
                    {opcion.titulo}
                  </Typography>
                </Stack>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Capítulo" placeholder="Todos los capítulos" />
              )}
            />

            <FormControlLabel
              label="Solo códigos facturables"
              control={
                <Checkbox
                  checked={soloHojas}
                  onChange={(evento) => {
                    setSoloHojas(evento.target.checked);
                    volverABusqueda();
                  }}
                />
              }
            />

            <Box sx={{ flexGrow: 1 }} />

            {lista.length > 0 && !cargandoLista && (
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {lista.length === 1 ? '1 resultado' : `${lista.length} resultados`}
              </Typography>
            )}
          </Stack>
        </Stack>

        {/* Alto reservado: la barra de progreso no puede desplazar el layout. */}
        <Box sx={{ height: 3, flexShrink: 0 }}>
          {cargandoLista && <LinearProgress sx={{ height: 3 }} />}
        </Box>

        <Divider />

        <Stack direction="row" sx={{ flex: 1, minHeight: 0 }}>
          <Stack sx={{ flex: 1, minWidth: 0 }}>
            {modo === 'exploracion' && (
              <Stack
                spacing={1}
                direction="row"
                alignItems="center"
                sx={{
                  px: 2,
                  py: 1,
                  flexShrink: 0,
                  bgcolor: alpha(theme.palette.info.main, 0.08),
                  borderBottom: `solid 1px ${theme.palette.divider}`,
                }}
              >
                <Iconify
                  width={18}
                  icon="eva:folder-outline"
                  sx={{ flexShrink: 0, color: 'info.main' }}
                />

                <Typography variant="caption" noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                  Explorando:{' '}
                  <Box component="span" sx={{ fontWeight: 700 }}>
                    {exploracion.titulo || '…'}
                  </Box>
                </Typography>

                <Button
                  size="small"
                  color="inherit"
                  onClick={volverABusqueda}
                  startIcon={<Iconify width={16} icon="eva:arrow-back-fill" />}
                >
                  Resultados
                </Button>
              </Stack>
            )}

            <Box sx={{ p: 1, flex: 1, minHeight: 0, overflowY: 'auto', ...barraFina(theme) }}>
              {renderContenidoLista()}
            </Box>
          </Stack>

          {!esMovil && <Divider orientation="vertical" flexItem />}

          {!esMovil && renderPanelDetalle}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2.5, py: 2 }}>
        <Typography
          variant="caption"
          sx={{
            mr: 'auto',
            color: 'text.disabled',
            display: { xs: 'none', sm: 'block' },
            width: '25%',
          }}
        >
          ↑ ↓ recorrer <br />
          Enter usar - Esc cerrar
        </Typography>

        <Button color="inherit" variant="outlined" onClick={onCerrar} sx={{ width: '25%' }}>
          Cancelar
        </Button>

        <Button
          variant="contained"
          disabled={!resaltado}
          fullWidth
          onClick={() => confirmar(resaltado)}
          startIcon={<Iconify width={18} icon="eva:checkmark-fill" />}
        >
          Usar este diagnóstico
        </Button>
      </DialogActions>
    </Dialog>
  );
}

IcdAdvancedDialog.propTypes = {
  abierto: PropTypes.bool,
  onCerrar: PropTypes.func,
  idInicial: PropTypes.string,
  onSeleccionar: PropTypes.func,
  consultaInicial: PropTypes.string,
};
